import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import registeredComponent from 'utils/registeredComponent';
import { duckify, singleDuck } from 'utils/duckify';
import { customers, modals, users as usersDuck } from 'ducks';
import { apiFetch } from 'utils';
import ModalRoot from 'components/ModalRoot';
import StripeBankAccount from 'components/payments/stripe/StripeBankAccount';
import PaymentTestModeWarning from 'components/payments/paymentInputs/PaymentTestModeWarning';
import { RequiresSuperAdmin } from '../../shared/RequiresUser';
import FA from '../../../utils/FA';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons/faExclamationTriangle';
import classNames from 'classnames';
import {generateCheckboxObject} from "../../DefaultModals";
import moment from 'moment';
import DOMPurify from "dompurify";

const StripeCustomOnboarding = ({
  dispatch,
  currentUser,
  customer,
  reload
}) => {
  const isSetup = customer && customer.cc_payment_info.stripe_account_id;

  const isTestMode = customer.cc_payment_info.test_mode;
  const isVerified = customer.cc_payment_info.charges_enabled;
  const isSuperAdmin = currentUser.is_super_admin;
  const needsInfo = customer.cc_payment_info.needs_info;
  const stripeMigrationEnabled = customer.cc_payment_info.stripe_migration;
  const payoutsEnabled = customer.cc_payment_info.payouts_enabled;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMigrating = customer.cc_payment_info.stripe_migration;

  const [customerRefundsEnabled, setCustomerRefundsEnabled] = useState(false);

  const fetchStripeCustomer = async () => {
    setLoading(true);
    customer = await apiFetch.get({}, 'stripe_custom/account') || {};
    setCustomerRefundsEnabled(
      customer.settings &&
      customer.settings.payouts &&
      customer.settings.payouts.debit_negative_balances);
    setLoading(false);
  };
  useEffect(() => {
    async function fetchData() {
      await fetchStripeCustomer();
      setLoading(false);
    }
    fetchData();
  }, []);

  const isSetupAndRefundEnabled = () =>  (isSetup && customerRefundsEnabled);

  const stripeStatus = isSetup ?
    (isVerified ?
      (payoutsEnabled ?
        (needsInfo ?
          'Additional information required' :
          'Account verified, ready to accept payments!') :
        'Ready to accept payments, but additional customer data or bank account information is required to receive payouts.') :
      'Onboarding started, but not completed') :
    'Not yet started';

  const handleCreate = () => {
    dispatch(modals.actions.show('ExplicitVerificationModal', {
      title: 'Create Account',
      message: (
        <div>
          <p>
            By proceeding to create an account
          </p>

          {isMigrating &&
            <div><p>Additionally, by proceeding to create an account, we authorize Stripe to copy payment profile data
              (including card data) from our existing Stripe account to our new Finalsite Enrollment Payments account.</p>
              <p>This will facilitate the migration process by ensuring that families are able to continue using their
                existing payment profiles without interruption once the migration is complete.</p></div>}</div>
      ),
      confirmButtonType: 'primary',
      checkboxObjects: [
        generateCheckboxObject(0,  <span>Our school agrees to the&nbsp;
                                          <a href='https://stripe.com/connect-account/legal' rel='noreferrer' target='_blank'>
                                            Stripe Connected Account Agreement
                                          </a>
                                            .</span>, isSetup, isSetup),
        generateCheckboxObject(1, 'Finalsite Payments offers seamless payment collection and unlimited refunds. Refunded amounts, failed payments, and fees will first be deducted '
          + 'from any available balance in Stripe. In the event that the balance does not cover the refund or fee, funds will pull directly from your connected account. Note: Please ensure your '
          + 'bank account is set up for withdrawals to avoid payout disruption. I authorize Finalsite to electronically debit my account and, if necessary, electronically credit my account to '
          + 'correct erroneous debits.', customerRefundsEnabled, customerRefundsEnabled)
      ],
      onConfirm: async () => {
        setLoading(true);

        try {
          await apiFetch.post({}, `stripe_custom/account`, {});
          await apiFetch.post({}, `stripe_custom/debit_negative_balances`, {enabled: true});
          await window.location.reload();
        } catch (e) {
          setError(e.error);
        } finally {
          setLoading(false);
        }
      },
      onClose: () => {
        setLoading(false);
        dispatch(modals.actions.hide());
      }
    }));
  };

  if (!isSuperAdmin && isTestMode) {
    return <p id='test-mode-error'>Onboarding is not allowed in test mode.</p>
  }

  return <React.Fragment>
    <ModalRoot modals={{ StripeBankAccount }} />

    {
      error ? (
        <div className='alert alert-danger'>
          {error}
        </div>
      ) : null
    }

    <p>Follow the steps below to complete the Payments Setup.</p>

    <p className={classNames('onboarding-status', {loading})}><b>Status:</b> {stripeStatus}</p>


    {customer.stripe_error_message && <div><b>Finalsite Enrollment Payment Errors:</b>
      <span dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(customer.stripe_error_message)}}/>
      To address these errors, the account owner must click Step 2.
      This will redirect to the onboarding screen, where the relevant information can be updated. If you need further assistance, please contact Finalsite Enrollment Support.</div>}

    <PaymentTestModeWarning testMode={isTestMode} />

    {stripeMigrationEnabled && (
      <div className={'alert alert-warning'}>
        <h3>Finalsite Enrollment Payments Migration</h3>
        Please complete the onboarding steps below to begin your migration to the Finalsite Enrollment Payments gateway.
        Once complete, our team will touch base with the next steps.
      </div>
    )}

    <div className="onboarding-step create-account">
      <b>Step 1</b>
      <br />
      <button
        className={isSetupAndRefundEnabled() ? 'btn btn-success' : 'btn btn-primary'}
        disabled={loading || isSetupAndRefundEnabled()}
        onClick={() => { !(loading || isSetupAndRefundEnabled()) && handleCreate() }}
        type='button'
      >
        { (isSetup && customerRefundsEnabled) ? 'Completed' : 'Click here to create an account'}
      </button>
    </div>

    <div className="onboarding-step onboard">
      <b>Step 2</b>
      <br />
      <button
        className={isVerified ? 'btn btn-success' : 'btn btn-primary'}
        disabled={loading || !isSetup}
        onClick={isSetup ? () => { window.location.href = '/api/stripe_custom/onboard'; } : () => false}
        type='button'
      >
        {isVerified ? 'Update information' : 'Click here to onboard'}
      </button>
    </div>

  </React.Fragment>;
};

StripeCustomOnboarding.propTypes = {
  currentUser: PropTypes.shape({
    is_super_admin: PropTypes.bool
  }),
  customer: PropTypes.shape({
    cc_payment_info: PropTypes.shape({
      charges_enabled: PropTypes.bool,
      legacy: PropTypes.bool,
      needs_info: PropTypes.bool,
      stripe_account_id: PropTypes.string,
      stripe_migration: PropTypes.bool,
      test_mode: PropTypes.bool
    }),
    payment_config: PropTypes.shape({
      refunds_disabled: PropTypes.bool
    })
  }),
  dispatch: PropTypes.func,
  reload: PropTypes.func
};

StripeCustomOnboarding.defaultProps = {
  currentUser: {
    is_super_admin: false
  },
  customer: {
    cc_payment_info: {}
  },

  dispatch: () => false,
  reload: () => false
};

const mapDucksToProps = () => ({
  currentUser: singleDuck(usersDuck, 'current'),
  customer: singleDuck(customers, 'current')
});

const mapStateToProps = () => { };

const mapDispatchToProps = (dispatch) => ({
  reload: () => dispatch(customers.actions.markDirty()),
  updateCustomer: (customer, paymentConfig) => {
    dispatch(customers.actions.update(customer, { customer: { payment_config: paymentConfig }}))
  }
});

export default registeredComponent('StripeCustomOnboarding')(
  duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps)(StripeCustomOnboarding)
);
